import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { money } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Ticket, Trophy, CheckCircle, WhatsappLogo, DownloadSimple, FilePdf, FileXls, Camera, Trash, ArrowClockwise, Image as ImageIcon, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const Ball = ({ n, red }) => (
  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold font-mono-num shadow-sm ${red ? "bg-red-600 text-white ring-2 ring-red-300" : "bg-white text-emerald-900 border border-slate-200"}`}>
    {String(n).padStart(2, "0")}
  </span>
);

export const PowerballRow = ({ white = [], red, size = "md" }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid="powerball-row">
    {white.map((n, i) => <Ball key={i} n={n} />)}
    <span className="text-slate-300 font-bold px-0.5">+</span>
    <Ball n={red} red />
  </div>
);

const downloadTicket = async (code) => {
  try {
    const res = await api.get(`/me/raffle/tickets/${code}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `boleto_${code}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) { toast.error("Error"); }
};

const TicketCard = ({ t: tkt, tr }) => (
  <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/60 p-4" data-testid={`ticket-${tkt.code}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold flex items-center gap-1"><Ticket size={14} weight="fill" /> #{tkt.code}</span>
      <span className="flex items-center gap-1">
        {tkt.paid && <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full" data-testid={`ticket-paid-${tkt.code}`}>{tr("raffle_paid")}</span>}
        {tkt.status === "won" && <span className="text-[10px] bg-amber-400 text-amber-950 font-bold px-2 py-0.5 rounded-full">GANADOR</span>}
      </span>
    </div>
    <PowerballRow white={tkt.white} red={tkt.red} />
    {tkt.member_name ? <p className="text-xs text-slate-400 mt-2 truncate">{tkt.member_name} · {tkt.reference}</p> : null}
    <button type="button" onClick={() => downloadTicket(tkt.code)} data-testid={`download-ticket-${tkt.code}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">
      <DownloadSimple size={14} weight="bold" /> {tr("raffle_download")}
    </button>
  </div>
);

const waLink = (phone, text) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const full = digits.length === 10 ? "1" + digits : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
};

export const RafflePanel = () => {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ enabled: false, prize_amount: "", prize_currency: "DOP", auto_draw: false, tickets_per_amount: "", mult_savings: 1, mult_term: 1, mult_loan: 1, seniority_enabled: false, seniority_months: 6, seniority_max: 10, prize_4: "", prize_3: "", src_savings: true, src_term: true, src_loan: true, paid_enabled: false, ticket_price: "", ticket_currency: "DOP", paid_max: 20, paid_weekly_max: 0, draw_at: "", exact_prize: false, signature_name: "", signature_title: "", share_slogan: "" });
  const [data, setData] = useState(null);
  const [draws, setDraws] = useState([]);
  const [partic, setPartic] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/raffle/settings").then((r) => { setSettings(r.data); setForm({ ...r.data, prize_amount: String(r.data.prize_amount || ""), tickets_per_amount: String(r.data.tickets_per_amount || ""), mult_savings: r.data.mult_savings || 1, mult_term: r.data.mult_term || 1, mult_loan: r.data.mult_loan || 1, seniority_enabled: !!r.data.seniority_enabled, seniority_months: r.data.seniority_months || 6, seniority_max: r.data.seniority_max ?? 10, prize_4: String(r.data.prize_4 || ""), prize_3: String(r.data.prize_3 || ""), src_savings: r.data.src_savings !== false, src_term: r.data.src_term !== false, src_loan: r.data.src_loan !== false, paid_enabled: !!r.data.paid_enabled, ticket_price: String(r.data.ticket_price || ""), ticket_currency: r.data.ticket_currency || "DOP", paid_max: r.data.paid_max || 20, paid_weekly_max: r.data.paid_weekly_max || 0, draw_at: (r.data.draw_at || "").slice(0, 16), exact_prize: !!r.data.exact_prize, signature_name: r.data.signature_name || "", signature_title: r.data.signature_title || "", share_slogan: r.data.share_slogan || "" }); }).catch(() => {});
    api.get("/admin/raffle/tickets").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/raffle/draws").then((r) => setDraws(r.data)).catch(() => {});
    api.get("/admin/raffle/participation").then((r) => setPartic(r.data)).catch(() => {});
    api.get("/admin/raffle/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/admin/raffle/revenue").then((r) => setRevenue(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const refund = async (ref) => {
    if (!window.confirm(t("raffle_confirm_refund"))) return;
    try { await api.post(`/admin/raffle/purchases/${ref}/refund`); toast.success(t("saved")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/raffle/settings", { enabled: form.enabled, prize_amount: Number(form.prize_amount || 0), prize_currency: form.prize_currency, auto_draw: form.auto_draw, tickets_per_amount: Number(form.tickets_per_amount || 0), mult_savings: Number(form.mult_savings || 1), mult_term: Number(form.mult_term || 1), mult_loan: Number(form.mult_loan || 1), seniority_enabled: form.seniority_enabled, seniority_months: Number(form.seniority_months || 6), seniority_max: Number(form.seniority_max || 0), prize_4: Number(form.prize_4 || 0), prize_3: Number(form.prize_3 || 0), src_savings: form.src_savings, src_term: form.src_term, src_loan: form.src_loan, paid_enabled: form.paid_enabled, ticket_price: Number(form.ticket_price || 0), ticket_currency: form.ticket_currency, paid_max: Number(form.paid_max || 20), paid_weekly_max: Number(form.paid_weekly_max || 0), draw_at: form.draw_at || "", exact_prize: form.exact_prize, signature_name: form.signature_name || "", signature_title: form.signature_title || "", share_slogan: form.share_slogan || "" });
      toast.success(t("saved"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const drawNow = async () => {
    if (!window.confirm(t("raffle_confirm_draw"))) return;
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/raffle/draw");
      if (d.no_winner) toast.success(t("raffle_no_winner_toast"));
      else toast.success(`${t("raffle_winner")}: #${d.winning_ticket_code}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const currentDraw = draws.find((d) => d.week_start === data?.week_start);

  const claim = async (ws) => {
    try {
      await api.post(`/admin/raffle/draws/${ws}/claim`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const uploadClaimPhoto = async (ws, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/raffle/draws/${ws}/claim-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("saved"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeClaimPhoto = async (ws) => {
    try { await api.delete(`/admin/raffle/draws/${ws}/claim-photo`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadReport = async (fmt) => {
    try {
      const res = await api.get(`/admin/raffle/prizes-report/${fmt}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `reporte_premios.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const downloadWinner = async (ws, kind) => {
    try {
      const isImg = kind !== "certificate";
      const path = kind === "certificate" ? `/admin/raffle/draws/${ws}/certificate` : `/admin/raffle/draws/${ws}/share-image?fmt=${kind}`;
      const res = await api.get(path, { responseType: "blob" });
      const type = isImg ? "image/png" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = kind === "certificate" ? `certificado_${ws}.pdf` : `ganador_${ws}_${kind}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const shareWinner = async (d) => {
    try {
      const res = await api.get(`/admin/raffle/draws/${d.week_start}/share-image?fmt=square`, { responseType: "blob" });
      const file = new File([res.data], `ganador_${d.week_start}.png`, { type: "image/png" });
      const text = t("raffle_share_text").replace("{name}", d.winner_name || "").replace("{prize}", money(d.prize_amount, d.prize_currency));
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: t("raffle_share_title"), text });
        return;
      }
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ganador_${d.week_start}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      try { await navigator.clipboard.writeText(text); } catch (e) { /* ignore */ }
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      toast.success(t("raffle_share_fallback"));
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error("Error");
    }
  };

  const uploadSignature = async (fileObj) => {
    if (!fileObj) return;
    const fd = new FormData(); fd.append("file", fileObj);
    try { const r = await api.post("/admin/raffle/signature", fd, { headers: { "Content-Type": "multipart/form-data" } }); setSettings((s) => ({ ...(s || {}), signature_image_url: r.data.signature_image_url })); toast.success(t("saved")); }
    catch (e) { toast.error("Error"); }
  };

  const removeSignature = async () => {
    try { await api.delete("/admin/raffle/signature"); setSettings((s) => ({ ...(s || {}), signature_image_url: "" })); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div data-testid="raffle-panel">
      <div className="flex items-center gap-2 mb-6">
        <Ticket size={26} weight="duotone" className="text-emerald-700" />
        <div>
          <h2 className="font-display text-2xl text-emerald-900">{t("raffle_title")}</h2>
          <p className="text-sm text-slate-500">{t("raffle_desc")}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h3 className="font-semibold text-emerald-900">{t("raffle_settings")}</h3>
          <div className="flex items-center justify-between">
            <Label className="text-emerald-900">{t("raffle_enable")}</Label>
            <Switch data-testid="raffle-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
          </div>
          <div>
            <Label className="text-emerald-900">{t("raffle_prize")}</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="raffle-prize-input" type="number" value={form.prize_amount} onChange={(e) => setForm({ ...form, prize_amount: e.target.value })} className="h-11" />
              <Select value={form.prize_currency} onValueChange={(v) => setForm({ ...form, prize_currency: v })}>
                <SelectTrigger className="h-11 w-28" data-testid="raffle-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="DOP">RD$</SelectItem><SelectItem value="USD">US$</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/40 p-2.5 mt-2">
              <div><Label className="text-emerald-900 text-sm">{t("raffle_exact_prize")}</Label><p className="text-[11px] text-slate-400">{t("raffle_exact_hint")}</p></div>
              <Switch data-testid="raffle-exact-switch" checked={form.exact_prize} onCheckedChange={(v) => setForm({ ...form, exact_prize: v })} />
            </div>
            {!form.exact_prize && Number(settings?.rollover || 0) > 0 && (
              <p className="text-xs text-amber-700 mt-1.5 font-semibold" data-testid="raffle-rollover">{t("raffle_rollover")}: {money(settings.rollover, form.prize_currency)} · {t("raffle_pot")}: {money(Number(form.prize_amount || 0) + Number(settings.rollover || 0), form.prize_currency)}{settings.rollover_weeks ? ` · ${t("raffle_rollover_weeks")}: ${settings.rollover_weeks}` : ""}</p>
            )}
            {form.exact_prize && (
              <p className="text-xs text-emerald-700 mt-1.5 font-semibold" data-testid="raffle-exact-active">{t("raffle_exact_active")}: {money(Number(form.prize_amount || 0), form.prize_currency)}</p>
            )}
          </div>
          <div>
            <Label className="text-emerald-900">{t("raffle_secondary_prizes")}</Label>
            <p className="text-xs text-slate-400 mb-1.5">{t("raffle_secondary_hint")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-[11px] text-slate-500">{t("raffle_prize4")}</span><Input data-testid="raffle-prize4" type="number" value={form.prize_4} onChange={(e) => setForm({ ...form, prize_4: e.target.value })} className="h-10 mt-0.5" placeholder="0" /></div>
              <div><span className="text-[11px] text-slate-500">{t("raffle_prize3")}</span><Input data-testid="raffle-prize3" type="number" value={form.prize_3} onChange={(e) => setForm({ ...form, prize_3: e.target.value })} className="h-10 mt-0.5" placeholder="0" /></div>
            </div>
          </div>
          <div>
            <Label className="text-emerald-900">{t("raffle_per_amount")}</Label>
            <Input data-testid="raffle-per-amount-input" type="number" value={form.tickets_per_amount} onChange={(e) => setForm({ ...form, tickets_per_amount: e.target.value })} className="h-11 mt-1.5" placeholder="0" />
            <p className="text-xs text-slate-400 mt-1">{t("raffle_per_amount_hint")}</p>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-2.5">
            <div>
              <Label className="text-emerald-900">{t("raffle_sources")}</Label>
              <p className="text-xs text-slate-400">{t("raffle_sources_hint")}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{t("raffle_src_savings")}</span>
              <Switch data-testid="raffle-src-savings" checked={form.src_savings} onCheckedChange={(v) => setForm({ ...form, src_savings: v })} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{t("raffle_src_term")}</span>
              <Switch data-testid="raffle-src-term" checked={form.src_term} onCheckedChange={(v) => setForm({ ...form, src_term: v })} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{t("raffle_src_loan")}</span>
              <Switch data-testid="raffle-src-loan" checked={form.src_loan} onCheckedChange={(v) => setForm({ ...form, src_loan: v })} />
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-3" data-testid="raffle-paid-section">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-emerald-900">{t("raffle_paid_enable")}</Label>
                <p className="text-xs text-slate-500">{t("raffle_paid_hint")}</p>
              </div>
              <Switch data-testid="raffle-paid-switch" checked={form.paid_enabled} onCheckedChange={(v) => setForm({ ...form, paid_enabled: v })} />
            </div>
            {form.paid_enabled && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-[11px] text-slate-500">{t("raffle_ticket_price")}</span>
                  <div className="flex gap-1.5 mt-0.5">
                    <Input data-testid="raffle-ticket-price" type="number" min="0" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} className="h-10" placeholder="0" />
                    <Select value={form.ticket_currency} onValueChange={(v) => setForm({ ...form, ticket_currency: v })}>
                      <SelectTrigger className="h-10 w-24" data-testid="raffle-ticket-currency"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="DOP">RD$</SelectItem><SelectItem value="USD">US$</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <span className="text-[11px] text-slate-500">{t("raffle_paid_max")}</span>
                  <Input data-testid="raffle-paid-max" type="number" min="1" max="100" value={form.paid_max} onChange={(e) => setForm({ ...form, paid_max: e.target.value })} className="h-10 mt-0.5" />
                </div>
                <div>
                  <span className="text-[11px] text-slate-500">{t("raffle_weekly_max")}</span>
                  <Input data-testid="raffle-weekly-max" type="number" min="0" value={form.paid_weekly_max} onChange={(e) => setForm({ ...form, paid_weekly_max: e.target.value })} className="h-10 mt-0.5" placeholder="0" />
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-emerald-900">{t("raffle_multipliers")}</Label>
            <p className="text-xs text-slate-400 mb-1.5">{t("raffle_multipliers_hint")}</p>
            <div className="grid grid-cols-3 gap-2">
              <div><span className="text-[11px] text-slate-500">{t("raffle_mult_savings")}</span><Input data-testid="raffle-mult-savings" type="number" min="1" value={form.mult_savings} onChange={(e) => setForm({ ...form, mult_savings: e.target.value })} className="h-10 mt-0.5" /></div>
              <div><span className="text-[11px] text-slate-500">{t("raffle_mult_term")}</span><Input data-testid="raffle-mult-term" type="number" min="1" value={form.mult_term} onChange={(e) => setForm({ ...form, mult_term: e.target.value })} className="h-10 mt-0.5" /></div>
              <div><span className="text-[11px] text-slate-500">{t("raffle_mult_loan")}</span><Input data-testid="raffle-mult-loan" type="number" min="1" value={form.mult_loan} onChange={(e) => setForm({ ...form, mult_loan: e.target.value })} className="h-10 mt-0.5" /></div>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-emerald-900">{t("raffle_seniority")}</Label>
                <p className="text-xs text-slate-500">{t("raffle_seniority_hint")}</p>
              </div>
              <Switch data-testid="raffle-seniority-switch" checked={form.seniority_enabled} onCheckedChange={(v) => setForm({ ...form, seniority_enabled: v })} />
            </div>
            {form.seniority_enabled && (
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-[11px] text-slate-500">{t("raffle_seniority_months")}</span><Input data-testid="raffle-seniority-months" type="number" min="1" value={form.seniority_months} onChange={(e) => setForm({ ...form, seniority_months: e.target.value })} className="h-10 mt-0.5" /></div>
                <div><span className="text-[11px] text-slate-500">{t("raffle_seniority_max")}</span><Input data-testid="raffle-seniority-max" type="number" min="0" value={form.seniority_max} onChange={(e) => setForm({ ...form, seniority_max: e.target.value })} className="h-10 mt-0.5" /></div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-emerald-900">{t("raffle_auto_draw")}</Label>
              <p className="text-xs text-slate-400">{t("raffle_auto_hint")}</p>
            </div>
            <Switch data-testid="raffle-auto-switch" checked={form.auto_draw} onCheckedChange={(v) => setForm({ ...form, auto_draw: v })} />
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid="raffle-draw-schedule">
            <Label className="text-emerald-900">{t("raffle_draw_datetime")}</Label>
            <p className="text-xs text-slate-400 mb-1.5">{t("raffle_draw_datetime_hint")}</p>
            <Input data-testid="raffle-draw-at" type="datetime-local" value={form.draw_at || ""} onChange={(e) => setForm({ ...form, draw_at: e.target.value })} className="h-11" />
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-3" data-testid="raffle-signature-section">
            <p className="text-sm font-semibold text-emerald-900">{t("raffle_signature_title")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-[11px] text-slate-500">{t("raffle_signature_name")}</span><Input data-testid="raffle-signature-name" value={form.signature_name} onChange={(e) => setForm({ ...form, signature_name: e.target.value })} className="h-10 mt-0.5" placeholder="Lic. Juan Pérez" /></div>
              <div><span className="text-[11px] text-slate-500">{t("raffle_signature_role")}</span><Input data-testid="raffle-signature-title" value={form.signature_title} onChange={(e) => setForm({ ...form, signature_title: e.target.value })} className="h-10 mt-0.5" placeholder="Presidente" /></div>
            </div>
            <div>
              <span className="text-[11px] text-slate-500">{t("raffle_signature_image")}</span>
              <div className="mt-1 flex items-center gap-3">
                {settings?.signature_image_url ? (
                  <>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.signature_image_url}`} alt="firma" className="h-12 bg-white rounded border border-emerald-200 px-2" data-testid="raffle-signature-preview" />
                    <button type="button" onClick={removeSignature} data-testid="raffle-signature-remove" className="text-slate-400 hover:text-red-600" title={t("delete")}><Trash size={16} weight="bold" /></button>
                  </>
                ) : (
                  <label data-testid="raffle-signature-upload" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg cursor-pointer">
                    <Camera size={14} weight="bold" /> {t("raffle_signature_upload")}
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { uploadSignature(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>
            <div><span className="text-[11px] text-slate-500">{t("raffle_share_slogan")}</span><Input data-testid="raffle-share-slogan" value={form.share_slogan} onChange={(e) => setForm({ ...form, share_slogan: e.target.value })} className="h-10 mt-0.5" placeholder={t("raffle_share_slogan_ph")} /></div>
          </div>
          <Button data-testid="raffle-save-btn" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 w-full">{t("save")}</Button>
        </div>

        <div className="bg-emerald-900 text-white rounded-xl shadow-sm p-5 flex flex-col">
          <p className="text-[11px] uppercase tracking-wider text-emerald-200/70">{t("raffle_this_week")}</p>
          <p className="text-xs text-emerald-200/80">{data?.week_start} → {data?.week_end}</p>
          <p className="font-mono-num text-4xl font-bold mt-2">{data?.count ?? 0}</p>
          <p className="text-sm text-emerald-100/80">{t("raffle_tickets_count")}</p>
          {currentDraw ? (
            <div className="mt-4 pt-3 border-t border-white/15">
              <p className="text-[11px] uppercase tracking-wider text-amber-300 mb-2 flex items-center gap-1"><Trophy size={14} weight="fill" /> {t("raffle_winning_numbers")}</p>
              <PowerballRow white={currentDraw.white} red={currentDraw.red} />
              <p className="text-sm text-emerald-100 mt-2">{currentDraw.winner_name || "—"} · #{currentDraw.winning_ticket_code}</p>
            </div>
          ) : (
            <Button data-testid="raffle-draw-btn" onClick={drawNow} disabled={busy || !data?.count} className="mt-4 bg-amber-400 hover:bg-amber-300 text-amber-950 font-bold">{t("raffle_draw_now")}</Button>
          )}
        </div>
      </div>

      {partic && partic.ranking && partic.ranking.length > 0 && (
        <div className="mt-6" data-testid="raffle-participation">
          <h3 className="font-semibold text-emerald-900 mb-3">{t("raffle_participation")}</h3>
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-2">
            {partic.ranking.map((p, i) => (
              <div key={p.branch} className="flex items-center justify-between text-sm" data-testid={`partic-${p.branch}`}>
                <span className="text-emerald-900 flex items-center gap-2"><span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-400 text-amber-950" : "bg-emerald-50 text-emerald-700"}`}>{i + 1}</span>{p.branch}</span>
                <span className="font-mono-num font-semibold text-emerald-800">{p.tickets} {t("raffle_participation_tickets")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {revenue && revenue.totals && revenue.totals.length > 0 && (
        <div className="mt-6" data-testid="raffle-revenue">
          <h3 className="font-semibold text-emerald-900 mb-3">{t("raffle_revenue_title")}</h3>
          <div className="flex flex-wrap gap-3 mb-4" data-testid="raffle-revenue-totals">
            {revenue.totals.map((tt) => (
              <div key={tt.currency} className="rounded-xl bg-emerald-900 text-white px-5 py-3">
                <p className="text-[11px] uppercase tracking-wider text-emerald-200/70">{t("raffle_revenue_total")}</p>
                <p className="font-mono-num text-2xl font-bold">{money(tt.amount, tt.currency)}</p>
                <p className="text-xs text-emerald-200/80">{revenue.total_tickets} {t("raffle_participation_tickets")}</p>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("raffle_revenue_by_week")}</p>
              {revenue.by_week.map((w) => (
                <div key={w.week_start} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`revenue-week-${w.week_start}`}>
                  <span className="text-slate-600">{w.week_start} → {w.week_end}</span>
                  <span className="font-mono-num font-semibold text-emerald-800">{money(w.amount, w.currency)} <span className="text-xs text-slate-400">· {w.tickets}</span></span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("raffle_revenue_by_branch")}</p>
              {revenue.by_branch.map((b, i) => (
                <div key={b.branch} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`revenue-branch-${b.branch}`}>
                  <span className="text-emerald-900 flex items-center gap-2"><span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-400 text-amber-950" : "bg-emerald-50 text-emerald-700"}`}>{i + 1}</span>{b.branch}</span>
                  <span className="font-mono-num font-semibold text-emerald-800">{money(b.amount, b.currency)} <span className="text-xs text-slate-400">· {b.tickets}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {purchases.length > 0 && (
        <div className="mt-6" data-testid="raffle-admin-purchases">
          <h3 className="font-semibold text-emerald-900 mb-3">{t("raffle_admin_purchases")}</h3>
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-2">
            {purchases.map((p) => (
              <div key={p.reference} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`admin-purchase-${p.reference}`}>
                <div>
                  <p className="text-emerald-900 font-semibold">{p.member_name || "—"} · {p.quantity} · {money(p.total, p.currency)}{p.refunded && <span className="ml-2 text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{t("raffle_refunded")}</span>}</p>
                  <p className="text-xs text-slate-400">{p.reference} · {p.account_number} · {(p.created_at || "").slice(0, 10)}</p>
                </div>
                {!p.refunded && (
                  <button type="button" onClick={() => refund(p.reference)} data-testid={`refund-${p.reference}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg whitespace-nowrap"><ArrowClockwise size={14} weight="bold" /> {t("raffle_refund_btn")}</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {draws.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-emerald-900">{t("raffle_prizes_report")}</h3>            <div className="flex gap-2">
              <button type="button" onClick={() => downloadReport("pdf")} data-testid="raffle-report-pdf" className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg">
                <FilePdf size={15} weight="bold" /> {t("raffle_export_pdf")}
              </button>
              <button type="button" onClick={() => downloadReport("excel")} data-testid="raffle-report-excel" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg">
                <FileXls size={15} weight="bold" /> {t("raffle_export_excel")}
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {draws.map((d) => (
              <div key={d.week_start} className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`draw-${d.week_start}`}>
                <div>
                  <p className="text-xs text-slate-400">{d.week_start} → {d.week_end} · {d.total_tickets} boletos {d.auto ? "· auto" : ""}</p>
                  <div className="mt-1"><PowerballRow white={d.white} red={d.red} /></div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-emerald-900 flex items-center gap-1 justify-end"><Trophy size={16} weight="fill" className="text-amber-500" /> {d.no_winner ? t("raffle_no_winner") : (d.winner_name || "—")}</p>
                  <p className="text-xs text-slate-500 font-mono-num">{d.no_winner ? money(d.prize_amount, d.prize_currency) : `#${d.winning_ticket_code} · ${money(d.prize_amount, d.prize_currency)}`}</p>
                  {d.secondary_winners && d.secondary_winners.length > 0 && (
                    <p className="text-[11px] text-amber-700 mt-0.5" data-testid={`minor-winners-${d.week_start}`}>{t("raffle_minor_winners")}: {d.secondary_winners.length}</p>
                  )}
                  {d.winner_phone && waLink(d.winner_phone, `¡Felicidades ${d.winner_name}! Tu boleto #${d.winning_ticket_code} (${d.white.map((n) => String(n).padStart(2, "0")).join("-")} + ${String(d.red).padStart(2, "0")}) ganó el sorteo semanal de Grupo Estrella Hernández Inversiones. Premio: ${money(d.prize_amount, d.prize_currency)}. ¡Pasa a reclamarlo!`) && (
                    <a href={waLink(d.winner_phone, `¡Felicidades ${d.winner_name}! Tu boleto #${d.winning_ticket_code} (${d.white.map((n) => String(n).padStart(2, "0")).join("-")} + ${String(d.red).padStart(2, "0")}) ganó el sorteo semanal de Grupo Estrella Hernández Inversiones. Premio: ${money(d.prize_amount, d.prize_currency)}. ¡Pasa a reclamarlo!`)} target="_blank" rel="noreferrer" data-testid={`wa-winner-${d.week_start}`} className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-white bg-[#25D366] hover:bg-[#1eb958] px-2.5 py-1 rounded-full">
                      <WhatsappLogo size={14} weight="fill" /> {t("raffle_notify_whatsapp")}
                    </a>
                  )}
                  <button type="button" onClick={() => claim(d.week_start)} data-testid={`claim-${d.week_start}`} className={`inline-flex items-center gap-1 mt-1.5 ml-2 text-xs font-semibold px-2.5 py-1 rounded-full ${d.claimed ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    <CheckCircle size={14} weight={d.claimed ? "fill" : "bold"} /> {d.claimed ? t("raffle_claimed") : t("raffle_mark_claimed")}
                  </button>
                  <div className="flex items-center justify-end gap-2 mt-2">
                    {d.claim_photo_url ? (
                      <>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${d.claim_photo_url}`} target="_blank" rel="noreferrer" data-testid={`claim-photo-view-${d.week_start}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${d.claim_photo_url}`} alt={t("raffle_delivery_photo")} className="w-8 h-8 rounded object-cover border border-emerald-200" />
                          {t("raffle_view_photo")}
                        </a>
                        <button type="button" onClick={() => removeClaimPhoto(d.week_start)} data-testid={`claim-photo-remove-${d.week_start}`} className="text-slate-400 hover:text-red-600" title={t("raffle_remove_photo")} aria-label={t("raffle_remove_photo")}>
                          <Trash size={15} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <label data-testid={`claim-photo-upload-${d.week_start}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded-full cursor-pointer">
                        <Camera size={14} weight="bold" /> {t("raffle_upload_photo")}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadClaimPhoto(d.week_start, e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                    )}
                  </div>
                  {!d.no_winner && (
                    <div className="flex items-center justify-end gap-1.5 mt-2 flex-wrap" data-testid={`winner-assets-${d.week_start}`}>
                      <button type="button" onClick={() => downloadWinner(d.week_start, "certificate")} data-testid={`winner-cert-${d.week_start}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-full"><FilePdf size={13} weight="bold" /> {t("raffle_certificate")}</button>
                      <button type="button" onClick={() => downloadWinner(d.week_start, "square")} data-testid={`winner-share-sq-${d.week_start}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-full"><ImageIcon size={13} weight="bold" /> 1080²</button>
                      <button type="button" onClick={() => downloadWinner(d.week_start, "story")} data-testid={`winner-share-st-${d.week_start}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-full"><ShareNetwork size={13} weight="bold" /> {t("raffle_share_story")}</button>
                      <button type="button" onClick={() => shareWinner(d)} data-testid={`winner-share-${d.week_start}`} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#25D366] hover:bg-[#1eb958] px-2.5 py-1 rounded-full"><ShareNetwork size={13} weight="fill" /> {t("raffle_share_btn")}</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const MyRaffleTickets = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [latest, setLatest] = useState(null);
  const [pinfo, setPinfo] = useState(null);
  const [qty, setQty] = useState(1);
  const [acct, setAcct] = useState("");
  const [buying, setBuying] = useState(false);
  const [lastRef, setLastRef] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const loadTickets = () => api.get("/me/raffle/tickets").then((r) => setData(r.data)).catch(() => {});
  const loadPurchase = () => api.get("/me/raffle/purchase-info").then((r) => { setPinfo(r.data); setAcct((prev) => prev || (r.data.accounts?.[0]?.id || "")); }).catch(() => {});
  const loadPurchases = () => api.get("/me/raffle/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  useEffect(() => {
    loadTickets();
    api.get("/me/raffle/latest-draw").then((r) => setLatest(r.data)).catch(() => {});
    loadPurchase();
    loadPurchases();
  }, []); // eslint-disable-line

  const buy = async () => {
    if (!acct) { toast.error(t("raffle_pick_account")); return; }
    setBuying(true);
    try {
      const { data: d } = await api.post("/me/raffle/purchase", { from_account_id: acct, quantity: Number(qty) });
      toast.success(`${t("raffle_bought")} ${d.quantity} · ${money(d.total, d.currency)}`, { duration: 6000 });
      setLastRef(d.reference);
      setQty(1);
      loadTickets();
      loadPurchase();
      loadPurchases();
    } catch (e) { toast.error(e.response?.data?.detail || "Error", { duration: 7000 }); }
    finally { setBuying(false); }
  };

  const downloadReceipt = async (ref) => {
    try {
      const res = await api.get(`/me/raffle/purchase/${ref}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `recibo_boletos_${ref}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const downloadAll = async () => {
    try {
      const res = await api.get("/me/raffle/tickets-pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "mis_boletos.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  if (data && !data.enabled && (!data.tickets || data.tickets.length === 0)) {
    return <div className="mt-8 bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-slate-500" data-testid="raffle-disabled">{t("raffle_off")}</div>;
  }

  return (
    <div className="mt-6" data-testid="my-raffle">
      {data?.enabled && data?.week_end && (() => {
        if (data.drawn) {
          return (
            <div className="rounded-2xl bg-emerald-900 text-white p-4 mb-5 flex items-center gap-3" data-testid="raffle-countdown">
              <CheckCircle size={28} weight="fill" className="text-amber-300" />
              <p className="font-display text-lg font-bold">{t("raffle_drawn_done")}</p>
            </div>
          );
        }
        const dleft = Math.max(0, Math.ceil((new Date((data.draw_at || (data.week_end + "T21:00:00")) + "-04:00") - new Date()) / 86400000));
        return (
          <div className="rounded-2xl bg-emerald-900 text-white p-4 mb-5 flex items-center gap-3" data-testid="raffle-countdown">
            <Ticket size={28} weight="duotone" className="text-amber-300" />
            <p className="font-display text-lg font-bold">{dleft === 0 ? t("raffle_today") : `${t("raffle_countdown_pre")} ${dleft} ${dleft === 1 ? t("raffle_day") : t("raffle_days")} ${t("raffle_countdown_post")}`}</p>
          </div>
        );
      })()}
      {latest?.won && latest?.draw && (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 text-amber-950 p-5 mb-5 flex items-center gap-4" data-testid="raffle-won-banner">
          <Trophy size={40} weight="fill" />
          <div>
            <p className="font-display text-xl font-bold">{t("raffle_you_won")}</p>
            <p className="text-sm">{money(latest.draw.prize_amount, latest.draw.prize_currency)} · #{latest.draw.winning_ticket_code}</p>
          </div>
        </motion.div>
      )}

      {pinfo?.paid_enabled && (
        <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200 shadow-sm p-5 mb-5" data-testid="raffle-buy-card">
          <div className="flex items-center gap-2 mb-1">
            <Ticket size={22} weight="duotone" className="text-amber-600" />
            <h3 className="font-semibold text-emerald-900">{t("raffle_buy_title")}</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">{t("raffle_buy_hint")}: {money(pinfo.ticket_price, pinfo.ticket_currency)} · {t("raffle_buy_max")}: {pinfo.paid_max}{pinfo.weekly_remaining != null ? ` · ${pinfo.weekly_remaining} ${t("raffle_weekly_left")}` : ""}</p>
          {lastRef && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 mb-4" data-testid="raffle-receipt-banner">
              <span className="text-sm text-emerald-800 flex items-center gap-1.5"><CheckCircle size={16} weight="fill" /> {t("raffle_receipt_ready")} · {lastRef}</span>
              <button type="button" onClick={() => downloadReceipt(lastRef)} data-testid="raffle-download-receipt" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 whitespace-nowrap">
                <DownloadSimple size={14} weight="bold" /> {t("raffle_download_receipt")}
              </button>
            </div>
          )}
          {pinfo.accounts?.length ? (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <span className="text-[11px] text-slate-500">{t("raffle_buy_qty")}</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10" data-testid="raffle-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))}>−</Button>
                  <Input type="number" min="1" max={pinfo.paid_max} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(pinfo.paid_max, Number(e.target.value) || 1)))} className="h-10 w-20 text-center" data-testid="raffle-qty-input" />
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10" data-testid="raffle-qty-plus" onClick={() => setQty((q) => Math.min(pinfo.paid_max, q + 1))}>+</Button>
                </div>
              </div>
              <div className="min-w-[190px]">
                <span className="text-[11px] text-slate-500">{t("raffle_buy_account")}</span>
                <Select value={acct} onValueChange={setAcct}>
                  <SelectTrigger className="h-10 mt-0.5" data-testid="raffle-account-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pinfo.accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_number} · {money(a.balance, a.currency)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[11px] text-slate-500">{t("raffle_buy_total")}</p>
                <p className="font-mono-num text-lg font-bold text-emerald-800" data-testid="raffle-buy-total">{money((Number(pinfo.ticket_price) || 0) * qty, pinfo.ticket_currency)}</p>
              </div>
              <Button onClick={buy} disabled={buying} data-testid="raffle-buy-btn" className="bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold h-10">{buying ? "…" : t("raffle_buy_btn")}</Button>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="raffle-buy-no-account">{t("raffle_buy_no_account")}</p>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Ticket size={22} weight="duotone" className="text-emerald-700" />
          <h3 className="font-semibold text-emerald-900">{t("raffle_my_tickets")}</h3>
          {data?.tickets?.length ? (
            <button type="button" onClick={downloadAll} data-testid="download-all-tickets" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">
              <DownloadSimple size={14} weight="bold" /> {t("raffle_download_all")}
            </button>
          ) : null}
        </div>
        <p className="text-xs text-slate-400 mb-4">{t("raffle_week")}: {data?.week_start} → {data?.week_end} · {t("raffle_prize")}: {money(data?.prize_amount || 0, data?.prize_currency || "DOP")}</p>
        {!data?.tickets?.length ? (
          <p className="text-slate-400 py-6 text-center" data-testid="raffle-no-tickets">{t("raffle_no_tickets")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.tickets.map((tk) => <TicketCard key={tk.code} t={tk} tr={t} />)}
          </div>
        )}
      </div>

      {purchases.length > 0 && (
        <div className="mt-5 bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="raffle-purchases">
          <h3 className="font-semibold text-emerald-900 mb-3">{t("raffle_my_purchases")}</h3>
          <div className="space-y-2">
            {purchases.map((p) => (
              <div key={p.reference} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`purchase-${p.reference}`}>
                <div>
                  <p className="text-sm font-semibold text-emerald-900">{p.quantity} {t("raffle_participation_tickets")} · {money(p.total, p.currency)}{p.refunded && <span className="ml-2 text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{t("raffle_refunded")}</span>}</p>
                  <p className="text-xs text-slate-400">{p.reference} · {(p.created_at || "").slice(0, 10)}</p>
                </div>
                <button type="button" onClick={() => downloadReceipt(p.reference)} data-testid={`purchase-receipt-${p.reference}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"><DownloadSimple size={14} weight="bold" /> {t("raffle_download_receipt")}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {latest?.draw && (
        <div className="mt-5 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <p className="text-[11px] uppercase tracking-wider text-emerald-600 mb-2 flex items-center gap-1"><Trophy size={14} weight="fill" className="text-amber-500" /> {t("raffle_last_winner")} · {latest.draw.week_start} → {latest.draw.week_end}</p>
          <PowerballRow white={latest.draw.white} red={latest.draw.red} />
          <p className="text-sm text-slate-600 mt-2 flex items-center gap-1">{latest.won ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : null}{latest.draw.no_winner ? t("raffle_no_winner") : `${latest.draw.winner_name || "—"} · #${latest.draw.winning_ticket_code}`}</p>
        </div>
      )}
    </div>
  );
};
